import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Camera, ShieldCheck, FloppyDisk, User as UserIcon } from "@phosphor-icons/react";

const MAX_PHOTO_BYTES = 8 * 1024 * 1024; // uploaded to /uploads/public-image (Pillow-optimised, DB-persisted)

/**
 * Re-usable Profile section: account info (name, phone, photo upload) + password change.
 * Used by MemberDashboard, ProfessionalDashboard and AdminDashboard.
 */
export default function ProfileSection() {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", picture: user?.picture || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (jpg / png / webp).");
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Photo must be under 8 MB.");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/public-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, picture: r.data?.url || "" }));
      toast.success("Photo uploaded — click Save changes to apply");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed. Please try again.");
    } finally {
      setUploadingPhoto(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/me", {
        name: form.name || null,
        phone: form.phone || null,
        picture: form.picture || null,
      });
      toast.success("Profile updated");
      if (refresh) refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save profile");
    } finally { setSavingProfile(false); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't change password");
    } finally { setSavingPw(false); }
  };

  if (!user) return null;
  const initials = user.name?.split(" ").slice(0, 2).map((p) => p[0]).join("").toUpperCase();

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="profile-section">
      {/* Account card with photo upload */}
      <div className="md-card" data-testid="profile-account-card">
        <div className="flex items-center gap-2 mb-4">
          <UserIcon size={20} weight="duotone" style={{ color: "var(--md-primary)" }} />
          <h3 className="text-lg font-serif">Account information</h3>
        </div>

        <div className="flex items-center gap-4 mb-5">
          {form.picture ? (
            <img src={form.picture} alt={form.name} className="w-20 h-20 rounded-full object-cover" style={{ boxShadow: "0 0 0 3px var(--md-primary)" }} />
          ) : (
            <div className="w-20 h-20 rounded-full flex items-center justify-center font-serif text-xl" style={{ background: "var(--md-bg-alt)", color: "var(--md-primary)", boxShadow: "0 0 0 3px var(--md-primary)" }}>{initials}</div>
          )}
          <div>
            <input type="file" accept="image/*" ref={fileRef} onChange={onFile} className="hidden" data-testid="profile-photo-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploadingPhoto} className="btn-outline text-xs" data-testid="profile-photo-pick">
              <Camera size={14} weight="bold" /> {uploadingPhoto ? "Uploading…" : "Upload photo"}
            </button>
            <p className="text-xs mt-1.5" style={{ color: "var(--md-text-muted)" }}>JPG / PNG / WEBP · up to 8 MB</p>
          </div>
        </div>

        <form onSubmit={saveProfile} className="space-y-3">
          <div>
            <label className="md-label-eyebrow block mb-1.5">Full name</label>
            <input className="md-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="profile-name" />
          </div>
          <div>
            <label className="md-label-eyebrow block mb-1.5">Email</label>
            <input className="md-input" value={user.email} disabled style={{ opacity: 0.7 }} data-testid="profile-email" />
          </div>
          <div>
            <label className="md-label-eyebrow block mb-1.5">Phone (optional)</label>
            <input type="tel" className="md-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" />
          </div>
          <button type="submit" disabled={savingProfile} className="btn-primary text-sm" data-testid="profile-save">
            <FloppyDisk size={14} weight="bold" /> {savingProfile ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>

      {/* Password */}
      <div className="md-card" data-testid="profile-password-card">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={20} weight="duotone" style={{ color: "var(--md-primary)" }} />
          <h3 className="text-lg font-serif">Change password</h3>
        </div>
        <form onSubmit={savePw} className="space-y-3">
          <div>
            <label className="md-label-eyebrow block mb-1.5">Current password</label>
            <input type="password" className="md-input" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="profile-current-pw" />
          </div>
          <div>
            <label className="md-label-eyebrow block mb-1.5">New password (min 6 chars)</label>
            <input type="password" minLength={6} className="md-input" required value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="profile-new-pw" />
          </div>
          <button type="submit" disabled={savingPw} className="btn-primary text-sm" data-testid="profile-change-pw">
            {savingPw ? "Saving…" : "Update password"}
          </button>
          <p className="text-xs" style={{ color: "var(--md-text-muted)" }}>You&apos;ll stay signed in after changing your password.</p>
        </form>
      </div>
    </div>
  );
}
